import api from 'api';
import { errorMessageFactory } from 'factories/api';
import type { ActivityFetchType, ActivityQuery, ActivityRaw } from 'redux/modules/activities.types';
import {
  clearActivitiesCreator,
  fetchedActivities,
  wrapFetching,
} from 'redux/modules/activities.types';
import { getActivityEntities, getActivityResult } from 'redux/selectors/activities';
import type { IState } from 'redux/state';
import type { ActivitySchemaKeys } from 'schema/activities';
import { PROJECT_ACTIVITY_SCHEMA_KEY, USER_ACTIVITY_SCHEMA_KEY } from 'schema/activities';
import type { Thunk } from 'types/redux';
import { showToast } from './toasts.actions';

export const fetchActivities =
  (activityType: ActivityFetchType, seedQuery: ActivityQuery): Thunk<Promise<Array<ActivityRaw>>> =>
  async dispatch => {
    const query: ActivityQuery = {
      project_id: undefined,
      user_id: undefined,
      limit: 50,
      offset: 0,
      order: 'desc',
      ...seedQuery,
    };

    const params = {
      query,
      filter: activityType,
    };

    if (query.offset === 0) {
      dispatch(clearActivitiesCreator());
    }

    try {
      const result = await dispatch(wrapFetching(api.getActivities(params)));
      dispatch(fetchedActivities({ result, params }));
      return result;
    } catch (error) {
      dispatch(showToast({ type: 'danger', message: errorMessageFactory(error) }));
      throw error;
    }
  };

function getActivityResultForType(state: IState, type: ActivitySchemaKeys) {
  const result = getActivityResult(state);
  return result[type];
}

function getActivityEntitiesForType(state: IState, type: ActivitySchemaKeys) {
  const entities = getActivityEntities(state);
  return entities[type];
}

// Load project activities
export function fetchProjectsActivities({
  loadMore = false,
} = {}): Thunk<Promise<Array<ActivityRaw>>> {
  return (dispatch, getState) => {
    const state = getState();
    const projectResult = getActivityResultForType(state, PROJECT_ACTIVITY_SCHEMA_KEY);
    const offset = !loadMore ? 0 : projectResult.length || 0;
    return dispatch(fetchActivities('projects', { offset }));
  };
}

export function fetchProjectIdActivities(id: number): Thunk<Promise<Array<ActivityRaw>>> {
  return (dispatch, getState) => {
    const state = getState();
    const entities = getActivityEntitiesForType(state, PROJECT_ACTIVITY_SCHEMA_KEY);
    const result = getActivityResultForType(state, PROJECT_ACTIVITY_SCHEMA_KEY);

    // Offset by # activities already loaded for this project
    const offset = result.filter(key => entities[key].entity === id).length || 0;

    return dispatch(fetchActivities('projects', { project_id: id, offset }));
  };
}

export const fetchUsersActivities =
  ({ loadMore = false } = {}): Thunk<Promise<Array<ActivityRaw>>> =>
  (dispatch, getState) => {
    const state = getState();
    const userResult = getActivityResultForType(state, USER_ACTIVITY_SCHEMA_KEY);
    const offset = !loadMore ? 0 : userResult.length || 0;
    return dispatch(fetchActivities('users', { offset }));
  };

export const fetchActivitiesForUser =
  (user_id: number): Thunk<Promise<Array<ActivityRaw>>> =>
  (dispatch, getState) => {
    const state = getState();
    const entities = getActivityEntitiesForType(state, USER_ACTIVITY_SCHEMA_KEY);
    const result = getActivityResultForType(state, USER_ACTIVITY_SCHEMA_KEY);

    const offset = result.filter(id => entities[id].user === user_id).length || 0;

    return dispatch(fetchActivities('users', { user_id, offset }));
  };
